// Professional ERP Design System

export const COLORS = {
  // Primary brand colors aligned with modern ERP standards
  primary: {
    25: '#f8fafc',
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
    950: '#082f49',
  },
  // Comprehensive grayscale for professional interfaces
  gray: {
    25: '#fcfcfd',
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  // Success states - professional green
  success: {
    25: '#f7fef9',
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },
  // Warning states - professional amber
  warning: {
    25: '#fffcf5',
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },
  // Error states - professional red
  error: {
    25: '#fffbfa',
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  // Info states - professional blue
  info: {
    25: '#f8fafc',
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },
  // Additional semantic colors for ERP
  purple: {
    500: '#8b5cf6',
    600: '#7c3aed',
  },
  pink: {
    500: '#ec4899',
    600: '#db2777',
  },
  indigo: {
    500: '#6366f1',
    600: '#4f46e5',
  },
  // Professional neutral colors for backgrounds
  neutral: {
    white: '#ffffff',
    black: '#000000',
    transparent: 'transparent',
  },
};

export const SPACING = {
  xxs: '2px',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
};

export const TYPOGRAPHY = {
  fontFamily: {
    // Ant Design 5.0 optimized font stack for enterprise interfaces
    primary: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
    // Modern monospace stack for code and data
    mono: `'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace`,
    // Professional serif for documents
    serif: `'Times New Roman', Times, serif`,
    // Alternative modern UI fonts
    display: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
    number: `'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  },
  fontSize: {
    // Ant Design 5.0 aligned font scale
    xs: '12px', // Small labels, captions
    sm: '14px', // Default body text (Ant Design default)
    base: '14px', // Standard text
    md: '16px', // Medium emphasis
    lg: '16px', // Large body text
    xl: '20px', // Section headings
    '2xl': '24px', // Page headings
    '3xl': '32px', // Large headings
    '4xl': '40px', // Display headings
    '5xl': '48px', // Hero headings
    '6xl': '60px', // Extra large display
  },
  fontWeight: {
    light: 300, // Light text
    normal: 400, // Regular text
    medium: 500, // Medium emphasis
    semibold: 600, // Strong emphasis
    bold: 700, // Bold headings
    extrabold: 800, // Extra bold display
  },
  lineHeight: {
    // Optimized for readability and UI density
    tight: 1.2, // Dense layouts, compact components
    snug: 1.375, // Slightly condensed
    normal: 1.5, // Standard reading (Ant Design default)
    relaxed: 1.625, // Comfortable reading
    loose: 1.75, // Very readable, long-form content
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  // Professional text styles for ERP interfaces
  textStyles: {
    // Headings
    'heading-1': {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    'heading-2': {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    'heading-3': {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    'heading-4': {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    // Body text
    'body-large': {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    'body-medium': {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    'body-small': {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    // Labels and UI text
    'label-large': {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    'label-medium': {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    'label-small': {
      fontSize: '10px',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '0.025em',
    },
    // Data and numbers
    'data-large': {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.2,
      fontVariantNumeric: 'tabular-nums',
    },
    'data-medium': {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.3,
      fontVariantNumeric: 'tabular-nums',
    },
    'data-small': {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.4,
      fontVariantNumeric: 'tabular-nums',
    },
  },
};

/**
 * Convert hex to rgba for shadow generation
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Generate color-specific shadows for buttons and interactive elements
 */
export function generateColorShadows(colors: {
  primary: string;
  success: string;
  error: string;
  warning: string;
  info: string;
}) {
  return {
    // Focus/outline shadows
    primary: `0 0 0 2px ${hexToRgba(colors.primary, 0.2)}`,
    success: `0 0 0 2px ${hexToRgba(colors.success, 0.2)}`,
    error: `0 0 0 2px ${hexToRgba(colors.error, 0.2)}`,
    warning: `0 0 0 2px ${hexToRgba(colors.warning, 0.2)}`,
    info: `0 0 0 2px ${hexToRgba(colors.info, 0.2)}`,
    // Button shadows (elevated buttons)
    primaryButton: `0 14px 12px ${hexToRgba(colors.primary, 0.2)}`,
    successButton: `0 14px 12px ${hexToRgba(colors.success, 0.2)}`,
    errorButton: `0 14px 12px ${hexToRgba(colors.error, 0.2)}`,
    warningButton: `0 14px 12px ${hexToRgba(colors.warning, 0.2)}`,
    infoButton: `0 14px 12px ${hexToRgba(colors.info, 0.2)}`,
  };
}

export const SHADOWS = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  // Elevation shadows
  z1: '0px 1px 4px rgb(0 0 0 / 0.08)',
  z2: '0px 2px 8px rgb(0 0 0 / 0.12)',
  z3: '0px 4px 16px rgb(0 0 0 / 0.16)',
  /** Top-edge shadow for cards/footers; cast upward for separation from content above. */
  top: '0 -2px 8px rgb(0 0 0 / 0.08)',
};

export const BORDERS = {
  radius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
  },
  width: {
    thin: '1px',
    medium: '2px',
    thick: '4px',
  },
};

// Motion design following Ant Design principles
export const ANIMATIONS = {
  duration: {
    instant: '0.1s',
    fast: '0.15s',
    normal: '0.2s',
    slow: '0.3s',
    slower: '0.5s',
  },
  easing: {
    // Ant Design motion curves
    easeOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    easeIn: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
    easeInOut: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    // Professional motion curves
    easeOutQuint: 'cubic-bezier(0.23, 1, 0.32, 1)',
    easeInQuint: 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
    easeInOutQuint: 'cubic-bezier(0.86, 0, 0.07, 1)',
    // Spring-like motion
    easeOutBack: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    easeInBack: 'cubic-bezier(0.36, 0, 0.66, -0.56)',
    easeInOutBack: 'cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  },
  // Predefined animations for common UI patterns
  presets: {
    fadeIn: {
      duration: '0.2s',
      easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    slideUp: {
      duration: '0.3s',
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      from: { transform: 'translateY(20px)', opacity: 0 },
      to: { transform: 'translateY(0)', opacity: 1 },
    },
    scaleIn: {
      duration: '0.2s',
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      from: { transform: 'scale(0.95)', opacity: 0 },
      to: { transform: 'scale(1)', opacity: 1 },
    },
  },
};

export const LAYOUT = {
  sidebar: {
    width: {
      expanded: '280px',
      collapsed: '80px',
    },
    breakpoint: '1200px',
  },
  header: {
    height: '60px',
  },
  content: {
    maxWidth: '1440px',
    padding: SPACING.lg,
  },
};

export const Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modal: 1040,
  popover: 1050,
  tooltip: 1060,
  notification: 1070,
};

// Typography utilities for consistent text alignment
export const TEXT_ALIGN = {
  left: 'left',
  center: 'center',
  right: 'right',
  justify: 'justify',
  start: 'start',
  end: 'end',
} as const;

// Consistent text styles for different content types
export const TEXT_STYLES = {
  // Headings with proper alignment
  heading: {
    fontFamily: TYPOGRAPHY.fontFamily.primary,
    fontWeight: TYPOGRAPHY.fontWeight.bold,
    lineHeight: TYPOGRAPHY.lineHeight.tight,
    textAlign: TEXT_ALIGN.left,
    letterSpacing: TYPOGRAPHY.letterSpacing.tight,
  },
  // Body text with optimal readability
  body: {
    fontFamily: TYPOGRAPHY.fontFamily.primary,
    fontWeight: TYPOGRAPHY.fontWeight.normal,
    lineHeight: TYPOGRAPHY.lineHeight.normal,
    textAlign: TEXT_ALIGN.left,
    letterSpacing: TYPOGRAPHY.letterSpacing.normal,
  },
  // Data and numbers with tabular alignment
  data: {
    fontFamily: TYPOGRAPHY.fontFamily.number,
    fontWeight: TYPOGRAPHY.fontWeight.medium,
    lineHeight: TYPOGRAPHY.lineHeight.tight,
    textAlign: TEXT_ALIGN.right,
    letterSpacing: TYPOGRAPHY.letterSpacing.normal,
    fontVariantNumeric: 'tabular-nums',
  },
  // Labels and UI text
  label: {
    fontFamily: TYPOGRAPHY.fontFamily.primary,
    fontWeight: TYPOGRAPHY.fontWeight.medium,
    lineHeight: TYPOGRAPHY.lineHeight.snug,
    textAlign: TEXT_ALIGN.left,
    letterSpacing: TYPOGRAPHY.letterSpacing.wide,
  },
  // Code and monospace content
  code: {
    fontFamily: TYPOGRAPHY.fontFamily.mono,
    fontWeight: TYPOGRAPHY.fontWeight.normal,
    lineHeight: TYPOGRAPHY.lineHeight.normal,
    textAlign: TEXT_ALIGN.left,
    letterSpacing: TYPOGRAPHY.letterSpacing.normal,
    fontVariantLigatures: 'common-ligatures',
  },
} as const;

// Responsive typography scale
export const RESPONSIVE_TEXT = {
  xs: {
    fontSize: TYPOGRAPHY.fontSize.xs,
    lineHeight: TYPOGRAPHY.lineHeight.tight,
  },
  sm: {
    fontSize: TYPOGRAPHY.fontSize.sm,
    lineHeight: TYPOGRAPHY.lineHeight.snug,
  },
  base: {
    fontSize: TYPOGRAPHY.fontSize.base,
    lineHeight: TYPOGRAPHY.lineHeight.normal,
  },
  lg: {
    fontSize: TYPOGRAPHY.fontSize.lg,
    lineHeight: TYPOGRAPHY.lineHeight.relaxed,
  },
  xl: {
    fontSize: TYPOGRAPHY.fontSize.xl,
    lineHeight: TYPOGRAPHY.lineHeight.relaxed,
  },
} as const;
